import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import toast from 'react-hot-toast'
import { US, GB, EU, JP, BR } from 'country-flag-icons/react/3x2'
import { BaseModal } from 'components/organisms/modals/base-modal'
import { Button } from 'components/atoms/button'
import SelectSearchBox from 'components/molecules/select-search-box'
import { SupportedCurrencyType, useCreateSeriesMutation } from 'graphql/generated/scheme'
import { useStore } from 'hooks/useStore'
import { SeriesIdentityFields } from 'containers/rep-clubs/events/series-identity-fields'
import { trueErrorMessage } from 'utils/error'

// Same set as the event Create/Edit wizard's own currency picker (Create/components/Info.tsx) —
// this determines the currency of every ticket type sold under this series (its hidden parent
// event's Currency), same as picking a currency for a normal event.
const CURRENCY_FLAG_ICONS: Record<string, JSX.Element> = {
	USD: <US title="US" className="w-5 h-auto rounded-sm" />,
	GBP: <GB title="GB" className="w-5 h-auto rounded-sm" />,
	EUR: <EU title="EU" className="w-5 h-auto rounded-sm" />,
	JPY: <JP title="JP" className="w-5 h-auto rounded-sm" />,
	BRL: <BR title="BR" className="w-5 h-auto rounded-sm" />,
}

interface CreateSeriesModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	basePath: string
}

interface CreateSeriesFormValues {
	name: string
	currency: string
	avatarUrl: string
}

const schema = yup.object({
	name: yup.string().trim().required('Series name is required').max(80, 'Series name must be 80 characters or fewer'),
	currency: yup.string().trim().required().default('USD'),
	avatarUrl: yup.string().default(''),
})

export const CreateSeriesModal = ({ open, setOpen, basePath }: CreateSeriesModalProps) => {
	const navigate = useNavigate()

	const { supportedCurrencies } = useStore()

	const {
		register,
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors, isValid },
	} = useForm<CreateSeriesFormValues>({
		resolver: yupResolver(schema),
		mode: 'onChange',
		defaultValues: { name: '', currency: 'USD', avatarUrl: '' },
	})

	const nameValue = watch('name') ?? ''
	const [uploadingAvatar, setUploadingAvatar] = useState(false)

	useEffect(() => {
		if (!open) {
			reset({ name: '', currency: 'USD', avatarUrl: '' })
		}
	}, [open, reset])

	const [createSeries, { loading }] = useCreateSeriesMutation({
		fetchPolicy: 'no-cache',
		onCompleted: data => {
			const created = data?.eventSeries?.createSeries
			setOpen(false)
			if (created?.eventSeriesId) {
				navigate(`${basePath}/series/${created.slug ?? created.eventSeriesId}`)
			}
		},
		onError: error => {
			toast.error(trueErrorMessage(error))
		},
	})

	const onSubmit = handleSubmit(values => {
		createSeries({
			variables: { name: values.name.trim(), currency: values.currency, avatarUrl: values.avatarUrl || undefined },
		})
	})

	return (
		<BaseModal
			open={open}
			setOpen={setOpen}
			title="Create event series"
			size="md"
			headerClassName="!w-auto !mx-0 pr-12 text-left"
		>
			<form onSubmit={onSubmit} className="text-left">
				<p className="text-sm text-secondary-text mb-7">
					Group related events, like a tour or a multi-city campaign, to manage them together. You'll add events
					next.
				</p>

				<Controller
					control={control}
					name="avatarUrl"
					render={({ field }) => (
						<SeriesIdentityFields
							inputId="create-series-name"
							nameField={register('name')}
							nameLength={nameValue.length}
							nameError={errors.name?.message}
							avatarUrl={field.value}
							onAvatarChange={field.onChange}
							uploadingAvatar={uploadingAvatar}
							setUploadingAvatar={setUploadingAvatar}
							disabled={loading}
							autoFocusName
						/>
					)}
				/>

				<div className="mt-7 border-t border-translucent-plus-border pt-6">
					<span id="create-series-currency" className="block text-sm font-medium text-primary-text mb-2">
						Ticket currency
					</span>
					<div aria-labelledby="create-series-currency">
						<Controller
							control={control}
							name="currency"
							render={({ field }) => (
								<SelectSearchBox
									options={supportedCurrencies.map((c: SupportedCurrencyType) => ({
										label: `${c.symbol} ${c.code} — ${c.name}`,
										value: c.code,
										...(CURRENCY_FLAG_ICONS[c.code] ? { icon: CURRENCY_FLAG_ICONS[c.code] } : {}),
									}))}
									value={field.value ?? 'USD'}
									onChange={value => field.onChange(value ?? 'USD')}
									placeholder="Select currency"
								/>
							)}
						/>
					</div>
					<p className="mt-2 text-xs text-secondary-text">
						All series tickets are priced in this currency. You can't change it after the series is created.
					</p>
				</div>

				<div className="mt-8 flex items-center justify-end gap-3">
					<Button variant="secondary" onClick={() => setOpen(false)}>
						Cancel
					</Button>
					<Button type="submit" disabled={!isValid || loading || uploadingAvatar}>
						{loading ? 'Creating…' : 'Create series'}
					</Button>
				</div>
			</form>
		</BaseModal>
	)
}
